// src/pages/teams/TeamScheduleRedirect.jsx
//
// Smart redirector for /teams/:teamId/schedule. Finds the team's
// most relevant season schedule and bounces the user there. Used
// by the team-page TeamSubNav's Schedule tab so the sub-nav can
// link to a stable team-scoped URL without knowing which season
// is active.
//
// Resolution order matches ScheduleGateway (the cross-team variant):
//   1. Open or closed season (most active) → /seasons/{id}/schedule
//   2. Any season at all (archived included) → same
//   3. None → friendly empty state with copy that adapts to role
//      (parent: "your coach hasn't published yet"; coach: CTA to
//      create a season)

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  collection, query, where, orderBy, limit, getDocs,
} from 'firebase/firestore';
import { db } from '@/firebase/firebase';
import { useRole } from '@/context/RoleContext';
import { CalendarDaysIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import TeamSubNav from '@/components/team/TeamSubNav';

const HEAD_FONT = "'Barlow Condensed', sans-serif";

export default function TeamScheduleRedirect() {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const { teamRole } = useRole();
  const [state, setState] = useState({ phase: 'resolving', err: null });

  useEffect(() => {
    if (!teamId) {
      setState({ phase: 'no-team', err: null });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        // 1st preference: any active season (open or closed status).
        const active = await getDocs(
          query(
            collection(db, 'seasons'),
            where('teamId', '==', teamId),
            where('status', 'in', ['open', 'closed']),
            orderBy('createdAt', 'desc'),
            limit(1)
          )
        );
        if (cancelled) return;
        if (!active.empty) {
          navigate(`/seasons/${active.docs[0].id}/schedule`, { replace: true });
          return;
        }

        // 2nd preference: any season.
        const any = await getDocs(
          query(
            collection(db, 'seasons'),
            where('teamId', '==', teamId),
            orderBy('createdAt', 'desc'),
            limit(1)
          )
        );
        if (cancelled) return;
        if (!any.empty) {
          navigate(`/seasons/${any.docs[0].id}/schedule`, { replace: true });
          return;
        }

        setState({ phase: 'no-seasons', err: null });
      } catch (err) {
        if (cancelled) return;
        if (import.meta.env.DEV) {
          console.warn('[TeamScheduleRedirect] season lookup failed', err);
        }
        setState({ phase: 'error', err });
      }
    })();
    return () => { cancelled = true; };
  }, [teamId, navigate]);

  const role = teamRole(teamId);
  const isParent = role === 'parent';

  if (state.phase === 'resolving') {
    return (
      <div className="min-h-screen bg-[#f7faf5]">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 py-8 sm:py-10">
          <TeamSubNav teamId={teamId} />
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <ArrowPathIcon className="h-8 w-8 text-[#5a8a1f] animate-spin mb-3" />
            <p className="text-sm text-zinc-500">Loading schedule…</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f7faf5]">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 py-8 sm:py-10">
        <TeamSubNav teamId={teamId} />

        <div className="rounded-2xl border border-zinc-200 bg-white p-8 sm:p-12 text-center shadow-[0_1px_2px_0_rgba(13,31,20,0.04)]">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40">
            <CalendarDaysIcon className="h-7 w-7 text-[#5a8a1f]" />
          </div>
          <h2
            className="text-xl sm:text-2xl font-bold uppercase tracking-wide text-[#0d1f14]"
            style={{ fontFamily: HEAD_FONT }}
          >
            No schedule yet
          </h2>
          <p className="mx-auto mt-2 max-w-md text-sm text-zinc-500 leading-relaxed">
            {isParent
              ? "Your child's coach hasn't published a season schedule yet. You'll see games and practices here once they do."
              : "No season has been created for this team yet. Set one up to start scheduling games and practices."}
          </p>
          {!isParent && (
            <div className="mt-6">
              <button
                type="button"
                onClick={() => navigate('/seasons/create')}
                className="inline-flex items-center gap-2 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-5 py-2.5 text-sm font-bold uppercase shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] transition-all active:scale-[0.97]"
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
              >
                Create a season
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
